import classNames from "classnames";
import { lazy, Suspense, useEffect, useState } from "react";
import { NonPreviewableFile } from "components/NonPreviewableFile/NonPreviewableFile";
import {
  PdfViewer,
  PdfPageClickHandler,
  PdfScrollTarget,
} from "components/PdfViewer/PdfViewer";
import { PinMarker } from "components/DocumentAnnotation/PinMarker";
import { DocumentPin } from "components/DocumentAnnotation/DocumentAnnotationPanel";
import Loader, { CenteredLoader } from "components/DesignSystem/Loader/loader";
import {
  useGetFolderOrFileQuery,
  useGetPreviewUrlQuery,
} from "store/apis/previewUrl";
import { DOC, EXCEL } from "constants/fileTypes";
import {
  DOCUMENT_PREVIEW_STATUS_READY,
  DocumentPreviewStatus,
  GENERATING_PREVIEW_STATUSES,
  WORD_PREVIEW_POLL_INTERVAL_MS,
  WORD_PREVIEW_POLL_TIMEOUT_MS,
} from "constants/documentPreviewStatus";

const SpreadsheetPreview = lazy(
  () => import("components/SpreadsheetPreview/SpreadsheetPreview")
);

type DocViewerProps = {
  url: string;
  fileType: string;
  name: string;
  // URL fetching — provide fileId + groupId when url isn't available yet
  fileId?: string;
  groupId?: string;
  // Annotation props — all optional, omit for plain read-only usage
  pins?: DocumentPin[];
  onPinClick?: (pinId: string) => void;
  onDocumentClick?: (x: number, y: number, pageNumber: number) => void;
  pageOverlayExtra?: (pageNumber: number) => React.ReactNode;
  // PDF-specific
  containerWidth?: number;
  scrollTo?: PdfScrollTarget | null;
};

const ImageDocViewer = ({
  url,
  name,
  pins = [],
  onPinClick,
  onDocumentClick,
  pageOverlayExtra,
}: {
  url: string;
  name: string;
  pins?: DocumentPin[];
  onPinClick?: (pinId: string) => void;
  onDocumentClick?: (x: number, y: number, pageNumber: number) => void;
  pageOverlayExtra?: (pageNumber: number) => React.ReactNode;
}) => {
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onDocumentClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    onDocumentClick(x, y, 1);
  };

  return (
    <div
      className="t-flex t-items-center t-justify-center t-w-full t-h-full t-overflow-hidden t-relative"
      onClick={handleClick}
      style={{ cursor: onDocumentClick ? "crosshair" : "default" }}
    >
      <img
        src={url}
        alt={name}
        className="t-object-contain t-max-h-full t-max-w-full"
      />
      {pins.map((pin) => (
        <PinMarker
          key={pin.id}
          number={pin.number}
          isActive={pin.isActive}
          onClick={() => onPinClick?.(pin.id)}
          style={{
            left: `${pin.x}%`,
            top: `${pin.y}%`,
            transform: "translate(-50%, -50%)",
          }}
        />
      ))}
      {pageOverlayExtra?.(1)}
    </div>
  );
};

export const DocViewer = ({
  url: urlProp,
  fileType,
  name,
  fileId,
  groupId,
  pins = [],
  onPinClick,
  onDocumentClick,
  pageOverlayExtra,
  containerWidth = 0,
  scrollTo,
}: DocViewerProps) => {
  const shouldFetch = !urlProp && !!fileId && !!groupId;
  const isWord = fileType === DOC;

  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [hasPollTimedOut, setHasPollTimedOut] = useState(false);

  const { data: fileObject, isLoading: isFileObjectLoading } =
    useGetFolderOrFileQuery(
      { groupId: groupId!, fileId: fileId! },
      {
        skip: !isWord || !fileId || !groupId,
        pollingInterval:
          isGeneratingPreview && !hasPollTimedOut
            ? WORD_PREVIEW_POLL_INTERVAL_MS
            : 0,
      }
    );
  const previewStatus = fileObject?.preview_status;
  const isPreviewStatusGenerating = Boolean(
    previewStatus && GENERATING_PREVIEW_STATUSES.has(previewStatus)
  );
  const isWaitingForWordPreview =
    isWord &&
    (isFileObjectLoading || (isPreviewStatusGenerating && !hasPollTimedOut));

  const { data, isLoading } = useGetPreviewUrlQuery(
    { groupId: groupId!, fileId: fileId! },
    { skip: !shouldFetch || isWaitingForWordPreview }
  );

  useEffect(() => {
    if (!isWord) {
      setIsGeneratingPreview(false);
      return;
    }
    setIsGeneratingPreview(
      GENERATING_PREVIEW_STATUSES.has(previewStatus as DocumentPreviewStatus)
    );
  }, [isWord, previewStatus, fileId]);

  useEffect(() => {
    if (!isGeneratingPreview) {
      setHasPollTimedOut(false);
      return;
    }
    const timer = setTimeout(
      () => setHasPollTimedOut(true),
      WORD_PREVIEW_POLL_TIMEOUT_MS
    );
    return () => clearTimeout(timer);
  }, [isGeneratingPreview, fileId]);

  const url = urlProp || data?.preview_url;

  if (isLoading || (isWord && isFileObjectLoading) || !url) {
    return (
      <div className="t-flex t-items-center t-justify-center t-h-full">
        <Loader />
      </div>
    );
  }

  if (isWord && isGeneratingPreview && !hasPollTimedOut) {
    return <CenteredLoader />;
  }

  const isWordReady = isWord && previewStatus === DOCUMENT_PREVIEW_STATUS_READY;

  if (isWord && !isWordReady) {
    // FAILED, or gave up waiting - the fetched url is still the original .docx.
    return (
      <div className="t-flex t-items-center t-justify-center t-h-full t-w-full t-px-10">
        <NonPreviewableFile name={name} preview_url={url} fullWidth />
      </div>
    );
  }

  if (fileType === "PDF" || isWordReady) {
    const pageOverlay = (pageNumber: number) => (
      <>
        {pins
          .filter((p) => p.pageNumber === pageNumber)
          .map((pin) => (
            <PinMarker
              key={pin.id}
              number={pin.number}
              isActive={pin.isActive}
              onClick={() => onPinClick?.(pin.id)}
              style={{
                position: "absolute",
                left: `${pin.x}%`,
                top: `${pin.y}%`,
                transform: "translate(-50%, -50%)",
              }}
            />
          ))}
        {pageOverlayExtra?.(pageNumber)}
      </>
    );

    const handlePageClick: PdfPageClickHandler | undefined = onDocumentClick
      ? (x, y, pageNumber) => onDocumentClick(x, y, pageNumber)
      : undefined;

    return (
      <PdfViewer
        url={url}
        containerWidth={containerWidth}
        scrollTo={scrollTo}
        pageOverlay={pageOverlay}
        onPageClick={handlePageClick}
      />
    );
  }

  if (fileType === "IMAGE") {
    return (
      <ImageDocViewer
        url={url}
        name={name}
        pins={pins}
        onPinClick={onPinClick}
        onDocumentClick={onDocumentClick}
        pageOverlayExtra={pageOverlayExtra}
      />
    );
  }

  if (fileType === EXCEL) {
    return (
      <Suspense fallback={<CenteredLoader />}>
        <SpreadsheetPreview preview_url={url} name={name} fullWidth />
      </Suspense>
    );
  }

  return (
    <div
      className={classNames(
        "t-flex t-items-center t-justify-center t-h-full t-w-full t-px-10",
        {
          "t-cursor-pointer": onDocumentClick,
          "t-cursor-default": !onDocumentClick,
        }
      )}
      onClick={onDocumentClick ? () => onDocumentClick(0, 0, 1) : undefined}
    >
      <NonPreviewableFile name={name} preview_url={url} fullWidth />
    </div>
  );
};
